import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import L, { MarkerCluster } from 'leaflet';
import { useMap } from 'react-leaflet';

import { Target } from '../../tmp';
import { getMarkerData, getMarkerDirection } from './markers/TargetMarkerChooser';
import { ctxPluto } from '@/contexts/PlutoContext';

import './TargetMarkers.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { isValidLatLong } from '@/utilities/latlong';
import { MinimalTarget } from '@/generated/model';

export function TargetMarkers({
  fullTargets,
  selectedTargetId,
  setSelectedTargetId,
}: {
  fullTargets: Target[] | undefined;
  selectedTargetId: number | undefined;
  setSelectedTargetId: Dispatch<SetStateAction<number | undefined>>;
}) {
  const ctx = useContext(ctxPluto);
  const map = useMap();
  const markers = useRef(new Map<number, L.Marker>());
  const [clusterLayer, setClusterLayer] = useState<L.MarkerClusterGroup | undefined>(undefined);
  const MARKER_ICON_SIZE = 32;
  const CLUSTER_ICON_SIZE = 36;

  useEffect(() => {
    if (!map) return;

    function createClusterIcon(cluster: MarkerCluster) {
      return L.divIcon({
        html: `${cluster.getChildCount()}`,
        className: 'leaflet-custom-cluster-icon',
        iconSize: [CLUSTER_ICON_SIZE, CLUSTER_ICON_SIZE],
      });
    }
    const cl = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 17,
      iconCreateFunction: createClusterIcon,
    });
    setClusterLayer(cl);
    map.addLayer(cl);
  }, [map]);

  useEffect(() => {
    if (!map || !clusterLayer) return;

    // ami kikerult torolni kell
    markers.current.forEach((marker, id) => {
      if (!fullTargets || !fullTargets.find((entry) => entry.target.id === id)) {
        clusterLayer.removeLayer(marker);
        markers.current.delete(id);
      }
    });

    const radToDeg = (rad: number): number => {
      return rad * (180.0 / Math.PI);
    };

    function getMarkerHtml(target: Target) {
      if (!target.currentPosition) return '';

      let markerHTML;
      if (target.currentPosition.icon) {
        markerHTML = getMarkerData(
          target.currentPosition.icon,
          MARKER_ICON_SIZE,
          MARKER_ICON_SIZE,
          target.currentPosition.color
        );
      } else {
        markerHTML = getMarkerData(
          target.target.category,
          MARKER_ICON_SIZE,
          MARKER_ICON_SIZE,
          target.currentPosition.color
        );
      }

      const directionRad = target.currentPosition.direction;
      if (!isNaN(directionRad)) {
        if (Math.cos(directionRad) < 0) {
          if (target.currentPosition.icon !== 'TACHO_REST') {
            markerHTML = `<div style="transform: scale(-1, 1)">${markerHTML}</div>`;
          }
        }
        const directionDeg = radToDeg(directionRad);
        markerHTML += getMarkerDirection(directionDeg);
      }
      return markerHTML;
    }

    function getTargetTooltipContent(target: MinimalTarget): string {
      if (target.description && target.description !== target.name) {
        return `${target.name}<span class="tooltip-subtitle">${target.description}</span>`;
      }
      return target.name;
    }

    fullTargets?.forEach((fullTarget) => {
      if (
        !fullTarget ||
        !fullTarget.target ||
        !fullTarget.currentPosition ||
        !isValidLatLong(fullTarget.currentPosition)
      ) {
        return;
      }

      const followedTargetClass = selectedTargetId == fullTarget.target.id ? 'selected-target-marker ' : '';
      const selectedTargetClass = ctx?.popupTargetId === fullTarget.target.id ? 'selected-tracking-target-icon' : '';

      const targetIcon = L.divIcon({
        html: `<div data-qa-element-id="target_icon_${fullTarget.target.name}">${getMarkerHtml(fullTarget)}</div>`,
        iconSize: [MARKER_ICON_SIZE, MARKER_ICON_SIZE],
        iconAnchor: [12, 12],
        popupAnchor: [0, 0],
        className: `${followedTargetClass} ${selectedTargetClass}`,
      });

      const directionRad = fullTarget.currentPosition.direction;
      const targetDirectionDown = !isNaN(directionRad) && Math.sin(directionRad) < 0;
      const tooltip = new L.Tooltip({
        className: 'target-marker-tooltip',
        content: getTargetTooltipContent(fullTarget.target),
        direction: targetDirectionDown ? 'top' : 'bottom',
        offset: targetDirectionDown ? [5, -12] : [5, 20],
        opacity: 0.9,
        permanent: true,
        interactive: true,
      });

      if (markers.current.get(fullTarget.target.id)) {
        const marker = markers.current.get(fullTarget.target.id);
        if (!marker) {
          console.log(`marker problem for ${fullTarget.target.id}, missing marker: ${marker}`);
          return;
        }

        try {
          const lastPosition = marker.getLatLng();
          if (
            lastPosition.lat !== fullTarget.currentPosition.latitude ||
            lastPosition.lng !== fullTarget.currentPosition.longitude
          ) {
            marker.setLatLng([fullTarget.currentPosition.latitude, fullTarget.currentPosition.longitude]);
          }
          marker.setIcon(targetIcon);
          // ha villog, akkor vissza kell terni az elozo megoldashoz
          // uj markernel csinalni tooltip-et
          // meglevo markernel pedig modositani a meglevot
          marker.bindTooltip(tooltip);

          // used for adding data-qa-element-id to tooltip component
          marker.on('tooltipopen', function (e) {
            e.tooltip.getElement()?.setAttribute('data-qa-element-id', `target_tooltip_${fullTarget.target.name}`);
          });
        } catch (e) {
          // a map variable in leaflet code sometimes initializes slower then needed PLUTO-1723
          console.error('Failed to update marker', e);
          window.location.reload();
          return;
        }
      } else {
        const marker = new L.Marker([fullTarget.currentPosition.latitude, fullTarget.currentPosition.longitude], {
          icon: targetIcon,
        });

        marker.on('dblclick', (e) => {
          map.setView(e.target.getLatLng(), 18);
          if (isPhoneOrHighScreen()) {
            e.target.openPopup();
          }
          setSelectedTargetId(fullTarget.target.id);
        });
        marker.bindTooltip(tooltip);
        marker.on('click', () => {
          ctx?.setPopupTargetId(fullTarget.target.id);
        });
        markers.current.set(fullTarget.target.id, marker);
        marker.addTo(clusterLayer);

        // used for adding data-qa-element-id to tooltip component
        marker.on('tooltipopen', function (e) {
          e.tooltip.getElement()?.setAttribute('data-qa-element-id', `target_tooltip_${fullTarget.target.name}`);
        });
      }
    });

    let timer: NodeJS.Timeout | undefined;
    function handleResize() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          map.invalidateSize(true);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
          // ezzel nem tudunk mit csinalni...
        }
        if (ctx?.popupTargetId) {
          const marker = markers.current.get(ctx.popupTargetId);
          marker?.getPopup()?.update();
        }
        timer = undefined;
      }, 100);
    }
    map.addEventListener('resize', handleResize);

    return () => {
      map.removeEventListener('resize', handleResize);
    };
  }, [fullTargets, selectedTargetId, map, clusterLayer]);

  return null;
}
